// The learner experience: home, assignments, progress, account.
import { loadParentCenterData } from "../data/account.js";
import {
  avatarLabel,
  avatarOptions,
  avatarPicture,
  learnerAdventureScene,
  pawnLabel,
  pawnOptions,
  pawnStorageKey,
  selectedAvatar,
  selectedAvatarStyle,
  selectedPawn
} from "../domain/avatars.js";
import {
  canonicalExerciseDescriptor,
  dedupeCanonicalExerciseRecords,
  exerciseQuestionCount
} from "../domain/curriculum.js";
import { learnerPresentation, missionNameForStep } from "../domain/learner.js";
import { startExercise } from "../features/exercise-player.js";
import { bindLanguage, copy, languageControl, t } from "../i18n.js";
import { closeModal, escapeHTML, showModal, showToast } from "../lib/dom.js";
import {
  difficultyBandInfo,
  difficultyDisplay,
  difficultySymbol,
  formatSeconds,
  localizedExerciseCount
} from "../lib/format.js";
import { logout, render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { appLayout, emptyExercises } from "../ui/layout.js";
import { parentDate } from "./parent.js";

function studentExerciseList(exercises = state.exercises) {
  const canonicalExercises = dedupeCanonicalExerciseRecords(exercises);
  if (!canonicalExercises.length) return emptyExercises();
  const topics = new Map();
  canonicalExercises.forEach(exercise => {
    const descriptor = canonicalExerciseDescriptor(exercise);
    if (!topics.has(descriptor.topicCode)) {
      topics.set(descriptor.topicCode, {
        ...descriptor,
        bands: new Map()
      });
    }
    const topic = topics.get(descriptor.topicCode);
    if (!topic.bands.has(descriptor.band)) topic.bands.set(descriptor.band, { info: descriptor.bandInfo, exercises: [] });
    topic.bands.get(descriptor.band).exercises.push(exercise);
  });
  const orderedTopics = [...topics.values()].sort((a, b) =>
    a.macroSort - b.macroSort || a.categorySort - b.categorySort || a.topicSort - b.topicSort || a.topicName.localeCompare(b.topicName)
  );
  return `<div class="student-assignment-groups">${orderedTopics.map(topic => {
    const topicExercises = [...topic.bands.values()].flatMap(group => group.exercises);
    const totalQuestions = topicExercises.reduce((sum, exercise) => sum + exerciseQuestionCount(exercise), 0);
    return `<details class="topic-group student-topic-group" open>
      <summary>
        <span class="student-topic-title">
          <small>${escapeHTML(topic.macroName)}</small>
          <strong>${escapeHTML(topic.topicName)}</strong>
        </span>
        <span class="topic-summary-meta">${localizedExerciseCount(topicExercises.length)} · ${totalQuestions} ${t("questions")}</span>
        <span class="chevron">⌄</span>
      </summary>
      <div class="student-topic-content">
        ${["base", "medio", "avanzato", "esperto"].map(code => {
          const group = topic.bands.get(code);
          if (!group) return "";
          const orderedExercises = group.exercises.sort((a, b) => Number(a.exerciseNumber || a.exercise_number || 1) - Number(b.exerciseNumber || b.exercise_number || 1));
          return `<section class="student-assignment-band">
            <div class="student-assignment-band-heading">
              <span>${group.info.icon}</span>
              <div><strong>${escapeHTML(group.info.label)}</strong><small>${localizedExerciseCount(orderedExercises.length)}</small></div>
            </div>
            <div class="exercise-list">
              ${orderedExercises.map((exercise, index) => {
                const number = index + 1;
                return `<button class="exercise-card student-assignment-card" data-exercise="${escapeHTML(exercise.assignment_id)}">
                  <span class="exercise-symbol">${number}</span>
                  <span>
                    <h3>${state.language === "it" ? "Attività" : "Activity"} ${number}</h3>
                    <p>${exerciseQuestionCount(exercise)} ${t("questions")}${exercise.due_at ? ` · ${state.language === "it" ? "entro" : "due"} ${parentDate(exercise.due_at)}` : ""}</p>
                  </span>
                  <span class="chevron">›</span>
                </button>`;
              }).join("")}
            </div>
          </section>`;
        }).join("")}
      </div>
    </details>`;
  }).join("")}</div>`;
}

function bindExerciseCards() {
  document.querySelectorAll("[data-exercise]").forEach(button => {
    button.addEventListener("click", () => startExercise(button.dataset.exercise));
  });
  document.querySelectorAll("[data-challenge-cell]").forEach(button => {
    button.addEventListener("click", () => showChallengePreview(Number(button.dataset.challengeCell)));
  });
}

export function homeView() {
  const canonicalExercises = dedupeCanonicalExerciseRecords(state.exercises);
  const xpPercent = Math.min(100, state.profile.experience / 5);
  const totalXP = state.profile.level * 500 + state.profile.experience;
  const questionsPerChallenge = 5;
  const completedSteps = Math.min(12, Math.floor(Number(state.profile.questions_solved || 0) / questionsPerChallenge));
  const currentStepNumber = Math.min(12, completedSteps + 1);
  const currentMissionName = missionNameForStep(completedSteps);
  const presentation = learnerPresentation();
  const pawn = selectedPawn();
  const learnerScene = learnerAdventureScene();
  const learnerSceneAlt = selectedAvatarStyle() === "female"
    ? (state.language === "it" ? "La bambina BrainMath corre verso una nuova missione" : "The BrainMath girl runs towards a new mission")
    : (state.language === "it" ? "Il bambino BrainMath corre verso una nuova missione" : "The BrainMath boy runs towards a new mission");
  const assignedQuestionCount = canonicalExercises.reduce((sum, exercise) => sum + exerciseQuestionCount(exercise), 0);
  const activeDifficultyCount = new Set(canonicalExercises.map(exercise => canonicalExerciseDescriptor(exercise).band)).size;
  const xpToNextLevel = Math.max(0, 500 - Number(state.profile.experience || 0));
  const worldName = presentation.mode === "playful"
    ? (state.language === "it" ? "Bosco dei Numeri" : "Number Woods")
    : presentation.mode === "explorer"
      ? (state.language === "it" ? "Regno delle Scoperte" : "Discovery Kingdom")
      : (state.language === "it" ? "Accademia BrainMath" : "BrainMath Academy");
  const journeyPositions = [
    [8, 81], [24, 68], [39, 79], [54, 68], [70, 78], [91, 67],
    [87.5, 35], [71, 29], [56, 42], [42.5, 29], [25, 31], [8, 18]
  ];
  const journey = Array.from({ length: 12 }, (_, index) => {
    const completed = index < completedSteps;
    const current = index === completedSteps;
    const locked = index > completedSteps;
    const next = index === completedSteps + 1;
    const content = completed ? "✓" : current ? `<span class="pawn-token" title="${escapeHTML(pawnLabel(pawn))}">${pawn.emoji}</span>` : "🔒";
    const className = `journey-cell challenge-cell ${completed ? "completed" : ""} ${current ? "current" : ""} ${locked ? "locked" : ""} ${next ? "next" : ""}`;
    const missionName = missionNameForStep(index);
    const [x, y] = journeyPositions[index];
    const label = current
      ? `<span class="journey-cell-label"><b>${state.language === "it" ? "Missione attuale" : "Current mission"}</b>${escapeHTML(missionName)}</span>`
      : next
        ? `<span class="journey-cell-label next-label"><b>${state.language === "it" ? "Prossima" : "Next"}</b>${escapeHTML(missionName)}</span>`
        : "";
    return current && canonicalExercises.length
      ? `<button class="${className}" style="--map-x:${x}%;--map-y:${y}%" data-challenge-cell="${index}" aria-label="${escapeHTML(missionName)}"><span>${content}</span><small>${index + 1}</small>${label}</button>`
      : `<div class="${className}" style="--map-x:${x}%;--map-y:${y}%" title="${escapeHTML(missionName)}"><span>${content}</span><small>${index + 1}</small>${label}</div>`;
  }).join("");
  const routeProgress = Math.max(0, Math.min(100, (completedSteps / 11) * 100));
  const nextChallenge = canonicalExercises.length
    ? challengeFromAvailableExercises(completedSteps)
    : null;
  const content = `
    <div class="student-sky" aria-hidden="true"><span>👧</span><span>⭐</span><span>🧒</span><span>✨</span><span>⭐</span><span>➕</span><span>🔢</span></div>
    <div class="content-grid student-adventure-grid">
      <section class="stack">
        <div class="card hero-card learner-${presentation.mode} student-hero student-mission-command">
          <div class="hero-content">
            <span class="student-ready-status"><i></i>${canonicalExercises.length ? (state.language === "it" ? "Missione pronta" : "Mission ready") : (state.language === "it" ? "In attesa di nuove attività" : "Waiting for new activities")}</span>
            <span class="hero-kicker">${escapeHTML(worldName)}</span>
            <h2>${state.language === "it" ? `Ciao ${escapeHTML(state.profile.nickname)}, dove arriviamo oggi?` : `Hi ${escapeHTML(state.profile.nickname)}, how far will we go today?`}</h2>
            <p>${canonicalExercises.length
              ? (state.language === "it" ? `La tappa “${escapeHTML(currentMissionName)}” è pronta. Scegli la difficoltà e conquista nuove stelle.` : `The “${escapeHTML(currentMissionName)}” stop is ready. Choose the difficulty and collect new stars.`)
              : (state.language === "it" ? "Non ci sono ancora domande disponibili. Puoi intanto esplorare i tuoi trofei." : "There are no questions available yet. You can explore your trophies in the meantime.")}</p>
            <div class="student-mission-ticket">
              <span class="student-ticket-number">${currentStepNumber}</span>
              <span><small>${state.language === "it" ? "Prossima missione" : "Next mission"}</small><strong>${escapeHTML(currentMissionName)}</strong><em>${questionsPerChallenge} ${t("questions")} · ${state.language === "it" ? "difficoltà a scelta" : "choose difficulty"}</em></span>
            </div>
            <button type="button" class="adventure-primary-action" id="continue-adventure" ${canonicalExercises.length ? "" : "disabled"}><span>▶</span>${state.language === "it" ? "Scopri la missione" : "Discover mission"}</button>
          </div>
          <div class="student-hero-profile">
            <div class="hero-character-scene character-cutout-stage">
              <img src="${learnerScene}" alt="${escapeHTML(learnerSceneAlt)}" decoding="async">
              <span class="mascot-bubble" aria-hidden="true">${pawn.emoji}</span>
              <i aria-hidden="true">✦</i><i aria-hidden="true">★</i>
            </div>
            <div class="student-level-card"><span>${presentation.symbol}</span><div><small>${t("level")} ${state.profile.level}</small><strong>${state.profile.experience}/500 XP</strong><i><b style="width:${xpPercent}%"></b></i></div></div>
          </div>
        </div>
        <div class="student-today-board">
          <button type="button" class="student-today-card homework" data-student-shortcut="exercises"><span>📚</span><div><small>${state.language === "it" ? "Da completare" : "To complete"}</small><strong>${canonicalExercises.length} ${state.language === "it" ? "attività della scuola" : "school activities"}</strong><em>${assignedQuestionCount} ${t("questions")} →</em></div></button>
          <button type="button" class="student-today-card progress" data-student-shortcut="progress"><span>🏆</span><div><small>${state.language === "it" ? "Verso il prossimo livello" : "Towards the next level"}</small><strong>${xpToNextLevel} XP ${state.language === "it" ? "da conquistare" : "left to earn"}</strong><em>${state.language === "it" ? "Apri i tuoi trofei" : "Open your trophies"} →</em></div></button>
        </div>
        <div class="card journey-card adventure-map-card">
          <div class="library-heading">
            <div><span class="eyebrow">${escapeHTML(worldName)}</span><h2>${state.language === "it" ? "La tua mappa delle missioni" : "Your mission map"}</h2><p>${state.language === "it" ? "Segui il sentiero: ogni missione completata apre una nuova tappa." : "Follow the trail: every completed mission opens a new stop."}</p></div>
            <strong class="map-xp-pill">${totalXP} XP</strong>
          </div>
          <div class="adventure-map">
            <div class="map-landmarks" aria-hidden="true"><span>🏕️</span><span>🌳</span><span>🏰</span><span>⛰️</span><span>🌟</span></div>
            <svg class="adventure-route" viewBox="0 0 800 360" preserveAspectRatio="none" aria-hidden="true">
              <path class="route-shadow" d="M65 290 C130 245 170 242 230 250 S330 300 385 255 S500 245 550 270 S660 300 730 242 C770 210 760 150 700 125 S590 95 530 125 S420 170 360 115 S250 70 195 110 S105 140 65 65" />
              <path class="route-base" d="M65 290 C130 245 170 242 230 250 S330 300 385 255 S500 245 550 270 S660 300 730 242 C770 210 760 150 700 125 S590 95 530 125 S420 170 360 115 S250 70 195 110 S105 140 65 65" />
              <path class="route-progress" pathLength="100" style="stroke-dasharray:${routeProgress} 100" d="M65 290 C130 245 170 242 230 250 S330 300 385 255 S500 245 550 270 S660 300 730 242 C770 210 760 150 700 125 S590 95 530 125 S420 170 360 115 S250 70 195 110 S105 140 65 65" />
            </svg>
            <span class="map-start" aria-hidden="true">🚩</span>
            <span class="map-finish" aria-hidden="true">🏆</span>
            <div class="journey-board">${journey}</div>
          </div>
          <div class="map-legend">
            <span><b class="legend-dot complete"></b>${state.language === "it" ? "completata" : "complete"}</span>
            <span><b class="legend-dot current"></b>${state.language === "it" ? "pronta" : "ready"}</span>
            <span><b class="legend-dot locked"></b>${state.language === "it" ? "bloccata" : "locked"}</span>
          </div>
          <div class="journey-next-action">
            ${nextChallenge
              ? `<div><strong>${escapeHTML(currentMissionName)}</strong><span>${state.language === "it" ? "Seleziona la tappa gialla per vedere la missione e avviarla." : "Select the yellow stop to preview and start the mission."}</span></div>`
              : `<p>${state.language === "it" ? "Appena avrai esercizi disponibili, la missione partirà dalla prossima tappa." : "When exercises are available, the mission starts from the next stop."}</p>`}
          </div>
        </div>
        <div class="card school-mission-card">
          <div class="school-mission-illustration character-cutout-stage"><img src="/brainmath-scenes/character-family-support.webp?v=20260725-cutouts" alt="${state.language === "it" ? "La famiglia BrainMath incoraggia i bambini" : "The BrainMath family encourages the children"}" loading="lazy" decoding="async"><span aria-hidden="true">🏫</span><i aria-hidden="true">✦</i><i aria-hidden="true">★</i></div>
          <div><span class="eyebrow">${state.language === "it" ? "Missioni della scuola" : "School missions"}</span><h2>${state.language === "it" ? "I compiti hanno una casa tutta loro" : "A dedicated home for assignments"}</h2><p>${state.language === "it" ? `Hai ${canonicalExercises.length} attività con ${assignedQuestionCount} domande, organizzate per argomento e difficoltà.` : `You have ${canonicalExercises.length} activities with ${assignedQuestionCount} questions, organized by topic and difficulty.`}</p><div class="school-mission-pills"><span>📘 ${canonicalExercises.length} ${state.language === "it" ? "attività" : "activities"}</span><span>🎯 ${activeDifficultyCount} ${state.language === "it" ? "difficoltà" : "difficulty levels"}</span></div><button type="button" class="secondary" data-student-shortcut="exercises">${state.language === "it" ? "Vai ai compiti" : "Go to assignments"} →</button></div>
        </div>
      </section>
      <aside class="stack student-adventure-aside">
        <div class="card adventure-backpack-card">
          <div class="backpack-heading"><span>🎒</span><div><small>${state.language === "it" ? "Il tuo zaino" : "Your backpack"}</small><h3>${state.language === "it" ? "Tesori raccolti" : "Collected treasures"}</h3></div></div>
          <div class="adventure-treasure-grid">
            <div><span>⭐</span><strong>${totalXP}</strong><small>XP</small></div>
            <div><span>🧠</span><strong>${state.profile.questions_solved}</strong><small>${state.language === "it" ? "risposte" : "answers"}</small></div>
            <div><span>🚩</span><strong>${completedSteps}</strong><small>${state.language === "it" ? "tappe" : "stops"}</small></div>
            <div><span>🎖️</span><strong>${Math.floor(Number(state.profile.questions_solved || 0) / 10)}</strong><small>${state.language === "it" ? "medaglie" : "medals"}</small></div>
          </div>
        </div>
        <div class="card next-reward-card">
          <div class="reward-orbit"><span>🎁</span><i></i><i></i><i></i></div>
          <span class="eyebrow">${state.language === "it" ? "Prossima ricompensa" : "Next reward"}</span>
          <h3>${state.language === "it" ? "Scrigno di livello" : "Level chest"}</h3>
          <p>${state.language === "it" ? `Ti mancano ${xpToNextLevel} XP per aprirlo.` : `${xpToNextLevel} XP left to open it.`}</p>
          <div class="reward-progress"><span style="width:${xpPercent}%"></span></div>
        </div>
      </aside>
    </div>`;
  appLayout(content, state.language === "it" ? "Avventura" : "Adventure", state.language === "it" ? "Esplora, impara e conquista nuove stelle." : "Explore, learn and collect new stars.");
  bindExerciseCards();
  document.querySelector("#continue-adventure")?.addEventListener("click", () => showChallengePreview(completedSteps));
  document.querySelectorAll("[data-student-shortcut]").forEach(button => button.addEventListener("click", () => {
    state.view = button.dataset.studentShortcut;
    render();
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }));
}

function challengeDifficultyOptions() {
  return [
    { value: 1, xp: 60, seconds: 120, icon: "🌱", label: state.language === "it" ? "Base" : "Basic" },
    { value: 3, xp: 80, seconds: 90, icon: "⭐", label: state.language === "it" ? "Medio" : "Intermediate" },
    { value: 6, xp: 110, seconds: 60, icon: "🚀", label: state.language === "it" ? "Avanzato" : "Advanced" },
    { value: 9, xp: 150, seconds: 30, icon: "🏆", label: state.language === "it" ? "Esperto" : "Expert" }
  ];
}

function defaultChallengeDifficulty(step) {
  return step < 3 ? 1 : step < 6 ? 3 : step < 9 ? 6 : 9;
}

function challengeFromAvailableExercises(step, requestedDifficulty = null) {
  const canonicalExercises = dedupeCanonicalExerciseRecords(state.exercises);
  const targetDifficulty = Number(requestedDifficulty || defaultChallengeDifficulty(step));
  const targetBand = difficultyBandInfo(targetDifficulty);
  const eligibleExercises = canonicalExercises.filter(exercise =>
    difficultyBandInfo(exercise.difficulty_level).code === targetBand.code
  );
  const sourceExercises = eligibleExercises.length ? eligibleExercises : canonicalExercises;
  const questions = sourceExercises.flatMap(exercise => (exercise.questions || []).map(question => ({
    ...question,
    topic: exercise.topic,
    difficulty_level: exercise.difficulty_level
  })));
  if (!questions.length) return null;
  const shuffled = questions.sort(() => Math.random() - 0.5).slice(0, Math.min(5, questions.length));
  const difficultyOption = challengeDifficultyOptions().find(option => option.value === targetDifficulty);
  return {
    assignment_id: null,
    activity_key: crypto.randomUUID(),
    title: missionNameForStep(step),
    topic: state.language === "it" ? "Percorso gioco" : "Game path",
    difficulty_level: targetDifficulty,
    source: "Challenge",
    author_name: "BrainMath",
    questions: shuffled,
    is_challenge: true,
    max_xp: difficultyOption?.xp || 80,
    time_limit: difficultyOption?.seconds || 90
  };
}

function startChallenge(step, difficulty = null) {
  const exercise = challengeFromAvailableExercises(step, difficulty);
  if (!exercise) {
    showToast(state.language === "it" ? "Non ci sono ancora domande disponibili per la challenge." : "No questions are available for the challenge yet.");
    return;
  }
  startExercise(null, exercise);
}

function showChallengePreview(step) {
  const selectedDifficulty = defaultChallengeDifficulty(step);
  const exercise = challengeFromAvailableExercises(step, selectedDifficulty);
  if (!exercise) {
    showToast(state.language === "it" ? "Non ci sono ancora domande disponibili per la challenge." : "No questions are available for the challenge yet.");
    return;
  }
  showModal(`
    <div class="modal-header">
      <div>
        <span class="eyebrow">${state.language === "it" ? "Missione stellare" : "Star mission"}</span>
        <h2>${escapeHTML(missionNameForStep(step))}</h2>
      </div>
      <button class="close-button" data-close-modal>×</button>
    </div>
    <div class="challenge-preview">
      <div class="challenge-preview-hero">
        <div class="challenge-preview-sparkles" aria-hidden="true"><span>✦</span><span>★</span><span>✦</span></div>
        <div class="challenge-preview-character"><img src="${learnerAdventureScene()}" alt="${state.language === "it" ? "Personaggio BrainMath in viaggio verso la missione" : "BrainMath character travelling towards the mission"}"><span aria-hidden="true">${selectedPawn().emoji}</span></div>
        <div>
          <strong>${state.language === "it" ? "Scegli come affrontarla" : "Choose how to play"}</strong>
          <p>${state.language === "it"
            ? "Ogni difficoltà cambia tempo, ricompensa e intensità della missione."
            : "Each difficulty changes the time, reward and mission intensity."}</p>
        </div>
      </div>
      <div class="challenge-difficulty-picker">
        ${challengeDifficultyOptions().map((option, optionIndex) => {
          const preview = challengeFromAvailableExercises(step, option.value);
          const disabled = !preview;
          return `<button type="button" class="challenge-difficulty-option difficulty-tone-${optionIndex + 1} ${option.value === selectedDifficulty ? "active" : ""}" data-challenge-difficulty="${option.value}" ${disabled ? "disabled" : ""}>
            <span class="difficulty-icon">${option.icon}</span>
            <span class="difficulty-copy"><strong>${escapeHTML(option.label)}</strong><small>${formatSeconds(option.seconds)} · +${option.xp} XP</small></span>
            <span class="difficulty-meter" aria-hidden="true">${[0, 1, 2, 3].map(level => `<i class="${level <= optionIndex ? "filled" : ""}"></i>`).join("")}</span>
            <span class="difficulty-check">✓</span>
          </button>`;
        }).join("")}
      </div>
      <div class="challenge-choice-summary" id="challenge-choice-summary">
        <span>${difficultySymbol(selectedDifficulty)}</span>
        <div><small>${state.language === "it" ? "Hai scelto" : "Selected"}</small><strong>${escapeHTML(difficultyDisplay(selectedDifficulty))}</strong></div>
      </div>
      <div class="stat-grid challenge-preview-stats" id="challenge-preview-stats">
        <div class="stat"><strong>${exercise.questions.length}</strong><span>${t("questions")}</span></div>
        <div class="stat"><strong>${formatSeconds(exercise.time_limit)}</strong><span>${state.language === "it" ? "tempo" : "time"}</span></div>
        <div class="stat"><strong>+${exercise.max_xp}</strong><span>XP max</span></div>
      </div>
      <button class="primary full challenge-start-button" id="start-preview-challenge"><span>▶</span>${state.language === "it" ? "Avvia la missione" : "Start mission"}</button>
    </div>`);
  let chosenDifficulty = selectedDifficulty;
  document.querySelectorAll("[data-challenge-difficulty]").forEach(button => {
    button.addEventListener("click", () => {
      chosenDifficulty = Number(button.dataset.challengeDifficulty);
      document.querySelectorAll("[data-challenge-difficulty]").forEach(item => item.classList.toggle("active", item === button));
      const preview = challengeFromAvailableExercises(step, chosenDifficulty);
      const stats = document.querySelector("#challenge-preview-stats");
      const summary = document.querySelector("#challenge-choice-summary");
      if (preview && stats) {
        stats.innerHTML = `
          <div class="stat"><strong>${preview.questions.length}</strong><span>${t("questions")}</span></div>
          <div class="stat"><strong>${formatSeconds(preview.time_limit)}</strong><span>${state.language === "it" ? "tempo" : "time"}</span></div>
          <div class="stat"><strong>+${preview.max_xp}</strong><span>XP max</span></div>`;
      }
      if (summary) {
        summary.innerHTML = `<span>${difficultySymbol(chosenDifficulty)}</span><div><small>${state.language === "it" ? "Hai scelto" : "Selected"}</small><strong>${escapeHTML(difficultyDisplay(chosenDifficulty))}</strong></div>`;
      }
    });
  });
  document.querySelector("#start-preview-challenge")?.addEventListener("click", () => {
    closeModal();
    startChallenge(step, chosenDifficulty);
  });
}

export function exercisesView() {
  const canonicalExercises = dedupeCanonicalExerciseRecords(state.exercises);
  const totalQuestions = canonicalExercises.reduce((sum, exercise) => sum + exerciseQuestionCount(exercise), 0);
  const topicCount = new Set(canonicalExercises.map(exercise => canonicalExerciseDescriptor(exercise).topicCode)).size;
  const content = `<div class="student-page-hero assignments-hero">
      <div><span class="eyebrow">${state.language === "it" ? "Missioni della scuola" : "School missions"}</span><h2>${state.language === "it" ? "La Biblioteca dei Compiti" : "Assignment Library"}</h2><p>${state.language === "it" ? "Scegli un argomento, poi affrontalo alla difficoltà che preferisci." : "Choose a topic, then play it at your preferred difficulty."}</p><div class="student-page-hero-stats"><span>📚 <strong>${canonicalExercises.length}</strong> ${state.language === "it" ? "attività" : "activities"}</span><span>❓ <strong>${totalQuestions}</strong> ${t("questions")}</span><span>🧩 <strong>${topicCount}</strong> ${state.language === "it" ? "argomenti" : "topics"}</span></div></div>
      <div class="page-hero-character character-cutout-stage"><img src="${learnerAdventureScene()}" alt="${state.language === "it" ? "Personaggio BrainMath pronto per una sfida di matematica" : "BrainMath character ready for a maths challenge"}" decoding="async"><span aria-hidden="true">📚</span><i aria-hidden="true">✦</i><i aria-hidden="true">★</i></div>
    </div>
    <div class="content-grid student-library-grid">
      <section class="card student-library-card"><div class="student-section-heading"><div><span class="eyebrow">${state.language === "it" ? "Scegli la tua sfida" : "Choose your challenge"}</span><h2>${t("pendingTitle")}</h2></div><span class="section-count">${topicCount}</span></div>${studentExerciseList(canonicalExercises)}</section>
      <aside class="stack">
        <div class="card difficulty-guide-card"><span class="guide-character">🧙‍♂️</span><span class="eyebrow">${state.language === "it" ? "Guida alle difficoltà" : "Difficulty guide"}</span><h3>${state.language === "it" ? "Quanto vuoi metterti alla prova?" : "How hard do you want to train?"}</h3><div class="difficulty-guide-list"><span><b>🌱</b><strong>${state.language === "it" ? "Base" : "Basic"}</strong><small>${state.language === "it" ? "Per iniziare con calma" : "Start gently"}</small></span><span><b>⭐</b><strong>${state.language === "it" ? "Medio" : "Intermediate"}</strong><small>${state.language === "it" ? "Una sfida equilibrata" : "A balanced challenge"}</small></span><span><b>🚀</b><strong>${state.language === "it" ? "Avanzato" : "Advanced"}</strong><small>${state.language === "it" ? "Serve concentrazione" : "Stay focused"}</small></span><span><b>🏆</b><strong>${state.language === "it" ? "Esperto" : "Expert"}</strong><small>${state.language === "it" ? "Per veri campioni" : "For true champions"}</small></span></div></div>
        <div class="card teacher-note-card"><span>💡</span><div><strong>${state.language === "it" ? "Ricorda" : "Remember"}</strong><p>${state.language === "it" ? "Puoi correggere una risposta sbagliata: imparare vale più che indovinare." : "You can correct a wrong answer: learning matters more than guessing."}</p></div></div>
      </aside>
    </div>`;
  appLayout(content, state.language === "it" ? "Compiti" : "Assignments", state.language === "it" ? "Le missioni preparate per te dalla scuola." : "Missions prepared for you by your school.");
  bindExerciseCards();
}

export function progressView() {
  const xpPercent = Math.min(100, state.profile.experience / 5);
  const solved = Number(state.profile.questions_solved || 0);
  const totalXP = Number(state.profile.level || 0) * 500 + Number(state.profile.experience || 0);
  const completedMissions = Math.min(12, Math.floor(solved / 5));
  const medals = Math.floor(solved / 10);
  const achievements = [
    { icon: "🌟", title: state.language === "it" ? "Prima scintilla" : "First sparkle", text: state.language === "it" ? "Completa 5 domande" : "Complete 5 questions", unlocked: solved >= 5 },
    { icon: "🧠", title: state.language === "it" ? "Mente allenata" : "Trained mind", text: state.language === "it" ? "Completa 20 domande" : "Complete 20 questions", unlocked: solved >= 20 },
    { icon: "🗺️", title: state.language === "it" ? "Esploratore" : "Explorer", text: state.language === "it" ? "Supera 5 missioni" : "Complete 5 missions", unlocked: completedMissions >= 5 },
    { icon: "👑", title: state.language === "it" ? "Campione BrainMath" : "BrainMath champion", text: state.language === "it" ? "Raggiungi 500 XP" : "Reach 500 XP", unlocked: totalXP >= 500 }
  ];
  const content = `<div class="student-page-hero progress-hero">
      <div><span class="eyebrow">${state.language === "it" ? "Sala dei trofei" : "Trophy room"}</span><h2>${state.language === "it" ? `Guarda quanta strada hai fatto, ${escapeHTML(state.profile.nickname)}!` : `Look how far you’ve come, ${escapeHTML(state.profile.nickname)}!`}</h2><p>${state.language === "it" ? "Ogni risposta fa crescere la tua esperienza e sblocca nuovi riconoscimenti." : "Every answer grows your experience and unlocks new rewards."}</p></div><div class="page-hero-art trophy-art" aria-hidden="true"><span>🏆</span><i>⭐</i><i>✨</i></div>
    </div>
    <div class="progress-kpi-grid">
      <div class="progress-kpi level-kpi"><span>👑</span><div><small>${t("level")}</small><strong>${state.profile.level}</strong></div></div>
      <div class="progress-kpi xp-kpi"><span>⭐</span><div><small>XP ${state.language === "it" ? "totali" : "total"}</small><strong>${totalXP}</strong></div></div>
      <div class="progress-kpi question-kpi"><span>🧠</span><div><small>${t("solved")}</small><strong>${solved}</strong></div></div>
      <div class="progress-kpi medal-kpi"><span>🎖️</span><div><small>${state.language === "it" ? "Medaglie" : "Medals"}</small><strong>${medals}</strong></div></div>
    </div>
    <div class="content-grid student-progress-grid">
      <section class="stack">
        <div class="card level-road-card"><div class="student-section-heading"><div><span class="eyebrow">${state.language === "it" ? "Verso il prossimo livello" : "Towards the next level"}</span><h2>${state.language === "it" ? "La tua energia stellare" : "Your star energy"}</h2></div><strong>${state.profile.experience}/500 XP</strong></div><div class="level-road"><span class="level-road-start">${state.profile.level}</span><div class="level-road-track"><span style="width:${xpPercent}%"></span><b style="left:${xpPercent}%">${selectedPawn().emoji}</b></div><span class="level-road-end">${Number(state.profile.level || 0) + 1}</span></div><p>${state.language === "it" ? `Ancora ${Math.max(0, 500 - Number(state.profile.experience || 0))} XP e raggiungerai il prossimo livello.` : `${Math.max(0, 500 - Number(state.profile.experience || 0))} XP to reach the next level.`}</p></div>
        <div class="card achievement-card"><div class="student-section-heading"><div><span class="eyebrow">${state.language === "it" ? "Collezione" : "Collection"}</span><h2>${state.language === "it" ? "Medaglie dell’avventura" : "Adventure badges"}</h2></div></div><div class="achievement-grid">${achievements.map(item => `<div class="achievement ${item.unlocked ? "unlocked" : "locked"}"><span>${item.unlocked ? item.icon : "🔒"}</span><strong>${escapeHTML(item.title)}</strong><small>${escapeHTML(item.text)}</small>${item.unlocked ? `<em>${state.language === "it" ? "Conquistata" : "Unlocked"}</em>` : ""}</div>`).join("")}</div></div>
      </section>
      <aside class="stack"><div class="card mission-summary-card"><span class="mission-summary-icon">🚩</span><span class="eyebrow">${state.language === "it" ? "Missioni" : "Missions"}</span><h3>${completedMissions}/12 ${state.language === "it" ? "tappe completate" : "stops completed"}</h3><div class="mission-dot-grid">${Array.from({length:12}, (_, index) => `<i class="${index < completedMissions ? "done" : index === completedMissions ? "current" : ""}">${index < completedMissions ? "✓" : index + 1}</i>`).join("")}</div></div><div class="card progress-tip-card"><span>🌈</span><h3>${state.language === "it" ? "Il segreto è continuare" : "Keep going"}</h3><p>${state.language === "it" ? "Anche correggere un errore è un passo avanti: ogni tentativo allena la mente." : "Correcting a mistake is progress too: every attempt trains your mind."}</p></div></aside>
    </div>`;
  appLayout(content, state.language === "it" ? "Trofei" : "Trophies", state.language === "it" ? "La storia dei tuoi progressi." : "The story of your progress.");
}

export function accountView() {
  const currentPawn = selectedPawn();
  const currentAvatarStyle = selectedAvatarStyle();
  const currentAvatar = selectedAvatar();
  const enrollment = state.enrollments.find(item => item.child_profile_id === state.profile?.id);
  const canChangeProfile = state.roles.includes("parent") && Boolean(state.profile);
  const schoolDescription = enrollment
    ? `${enrollment.school_name || ""}${enrollment.class_name ? ` · ${enrollment.class_name}` : ""}`
    : (state.language === "it" ? "Profilo di apprendimento" : "Learning profile");
  const content = `<div class="content-grid student-settings-grid">
    <section class="card student-account-card">
      <div class="student-profile-showcase"><span>${avatarPicture()}</span><div><small>${state.language === "it" ? "Il mio personaggio" : "My character"}</small><h2>${escapeHTML(state.profile.nickname)}</h2><p>${escapeHTML(schoolDescription)}</p></div></div>
      <div class="stat-grid">
        <div class="stat"><strong>${state.profile.level}</strong><span>${t("level")}</span></div>
        <div class="stat"><strong>${Number(state.profile.level || 0) * 500 + Number(state.profile.experience || 0)}</strong><span>XP</span></div>
        <div class="stat"><strong>${enrollment?.class_name ? escapeHTML(enrollment.class_name) : "—"}</strong><span>${state.language === "it" ? "Classe" : "Class"}</span></div>
      </div>
    </section>
    <aside class="card stack student-customizer-card">
      <div class="student-language-card"><span class="student-language-icon">🌍</span><div class="student-language-copy"><span class="eyebrow">${state.language === "it" ? "Lingua dell’avventura" : "Adventure language"}</span><h3>${state.language === "it" ? "Come vuoi giocare?" : "How do you want to play?"}</h3><p>${state.language === "it" ? "Menu, domande, suggerimenti e risultati useranno la lingua scelta." : "Menus, questions, hints and results will use your chosen language."}</p></div>${languageControl()}</div>
      ${state.profile ? `<div class="avatar-picker">
        <span class="eyebrow">${state.language === "it" ? "Icona giocatore" : "Player icon"}</span>
        <h3>${state.language === "it" ? "Personalizza profilo" : "Customize profile"}</h3>
        <div class="avatar-catalog-lock"><span>${currentAvatarStyle === "female" ? "👧" : "👦"}</span><div><strong>${currentAvatarStyle === "female" ? (state.language === "it" ? "Avatar femminili" : "Girl avatars") : (state.language === "it" ? "Avatar maschili" : "Boy avatars")}</strong><small>${state.language === "it" ? "Catalogo collegato al profilo" : "Catalogue linked to the profile"}</small></div><b>🔒</b></div>
        <div class="avatar-options">
          ${avatarOptions(currentAvatarStyle).map(avatar => `
            <button class="avatar-option ${avatar.code === currentAvatar.code ? "selected" : ""}" data-avatar="${avatar.code}" title="${escapeHTML(avatarLabel(avatar))}">
              <img src="${avatar.image}" alt="">
              <small>${escapeHTML(avatarLabel(avatar))}</small>
            </button>`).join("")}
        </div>
        <p>${state.language === "it" ? "Questa icona rende unico il profilo e compare nel menu e nella selezione profilo." : "This icon makes the profile unique and appears in the menu and profile selection."}</p>
      </div>` : ""}
      ${state.profile ? `<div class="pawn-picker">
        <span class="eyebrow">${state.language === "it" ? "Pedina gioco" : "Game pawn"}</span>
        <h3>${state.language === "it" ? "Scegli il personaggio" : "Choose character"}</h3>
        <div class="pawn-options">
          ${pawnOptions(currentAvatarStyle).map(pawn => `
            <button class="pawn-option ${pawn.code === currentPawn.code ? "selected" : ""}" data-pawn="${pawn.code}" title="${escapeHTML(pawnLabel(pawn))}">
              <span>${pawn.emoji}</span>
              <small>${escapeHTML(pawnLabel(pawn))}</small>
            </button>`).join("")}
        </div>
        <p>${state.language === "it" ? "La pedina appare nella casella gialla del percorso challenge." : "The pawn appears on the yellow challenge cell."}</p>
      </div>` : ""}
      ${canChangeProfile ? `<button class="secondary full" id="change-profile">🏡 ${state.language === "it" ? "Torna al Centro Famiglia" : "Back to Family Center"}</button>` : ""}
      <button class="primary full" id="account-logout">${t("logout")}</button>
    </aside>
  </div>`;
  appLayout(content, state.language === "it" ? "Il mio personaggio" : "My character", state.language === "it" ? "Scegli il tuo stile per rendere unica ogni avventura." : "Choose your style and make every adventure unique.");
  bindLanguage();
  document.querySelectorAll("[data-avatar]").forEach(button => {
    button.addEventListener("click", async () => {
      button.disabled = true;
      const avatarCode = button.dataset.avatar;
      const { error } = await supabase.rpc("update_child_avatar", {
        selected_child_profile: state.profile.id,
        selected_avatar: avatarCode
      });
      if (error) {
        button.disabled = false;
        showToast(error.message);
        return;
      }
      state.profile.avatar_symbol = avatarCode;
      const profileIndex = state.profiles.findIndex(profile => profile.id === state.profile.id);
      if (profileIndex >= 0) state.profiles[profileIndex] = { ...state.profiles[profileIndex], avatar_symbol: avatarCode };
      showToast(state.language === "it" ? "Avatar aggiornato e salvato." : "Avatar updated and saved.");
      accountView();
    });
  });
  document.querySelectorAll("[data-pawn]").forEach(button => {
    button.addEventListener("click", () => {
      localStorage.setItem(pawnStorageKey(), button.dataset.pawn);
      showToast(state.language === "it" ? "Pedina aggiornata." : "Pawn updated.");
      accountView();
    });
  });
  document.querySelector("#change-profile")?.addEventListener("click", async () => {
    state.profile = null;
    state.parentMode = state.roles.includes("parent");
    localStorage.removeItem("brainmath-profile");
    sessionStorage.removeItem("brainmath-parent-child-mode");
    if (state.parentMode) await loadParentCenterData();
    render();
  });
  document.querySelector("#account-logout").addEventListener("click", logout);
}
